import { Injectable, NgZone } from "@angular/core";
import { Location } from "@angular/common";
import { NavigationEnd, Router } from "@angular/router";
import { App } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { filter } from "rxjs/operators";

/**
 * Single coordinator for the Android hardware back button across the
 * Angular shell and any embedded React Native feature.
 *
 * React Native features report their internal navigation state via the
 * `rnNavigationStateChange` window event. When the hardware back button fires,
 * if an RN feature is active and can go back internally, the coordinator asks
 * the RN feature to pop. Otherwise Angular performs its own back navigation.
 */
@Injectable({ providedIn: "root" })
export class BackCoordinatorService {
  private backButtonListener?: PluginListenerHandle;

  // Current foreground feature state
  private activeFeatureId: string | null = null;
  private rnCanGoBack = false;

  private canGoBackSubject = new BehaviorSubject<boolean>(false);
  public canGoBack$: Observable<boolean> = this.canGoBackSubject.asObservable();
  private routerSubscription: Subscription;

  // Track Angular navigation history ourselves so we know whether there is an
  // actual back stack independent of browser forward entries or replaceUrl.
  private historyStack: string[] = [];
  private historyIndex = -1;

  constructor(
    private router: Router,
    private location: Location,
    private ngZone: NgZone
  ) {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.trackHistory((event as NavigationEnd).urlAfterRedirects);
        this.updateCanGoBack();
      });
  }

  async initialize() {
    if (this.backButtonListener) {
      return;
    }

    window.addEventListener(
      "rnNavigationStateChange",
      this.handleRnNavigationStateChange
    );

    this.backButtonListener = await App.addListener("backButton", () => {
      this.ngZone.run(() => this.handleHardwareBack());
    });
  }

  destroy() {
    window.removeEventListener(
      "rnNavigationStateChange",
      this.handleRnNavigationStateChange
    );
    if (this.backButtonListener) {
      this.backButtonListener.remove();
      this.backButtonListener = undefined;
    }
    this.routerSubscription?.unsubscribe();
  }

  /**
   * Called by the ReactNativeBundleComponent when the active feature changes.
   */
  setActiveFeature(featureId: string | null) {
    console.log(`[NAV] [BackCoordinator] Active feature set to: ${featureId}`);
    this.activeFeatureId = featureId;
    if (!featureId) {
      this.rnCanGoBack = false;
    }
    this.updateCanGoBack();
  }

  /**
   * Public action used by the footer back button. Mirrors handleHardwareBack()
   * but can also be invoked from a UI tap.
   */
  goBack() {
    this.handleHardwareBack();
  }

  /**
   * Track Angular navigations in a local stack so we can tell whether there is
   * an actual back entry, regardless of browser forward history or replaceUrl.
   */
  private trackHistory(url: string): void {
    // First navigation
    if (this.historyIndex === -1) {
      this.historyStack = [url];
      this.historyIndex = 0;
      return;
    }

    // Forward navigation via the browser forward button or location.forward()
    if (this.historyStack[this.historyIndex + 1] === url) {
      this.historyIndex++;
      return;
    }

    // Back navigation
    if (
      this.historyIndex > 0 &&
      this.historyStack[this.historyIndex - 1] === url
    ) {
      this.historyIndex--;
      return;
    }

    // New forward navigation in the middle of the stack: truncate forward entries.
    if (this.historyIndex < this.historyStack.length - 1) {
      this.historyStack = this.historyStack.slice(0, this.historyIndex + 1);
    }

    this.historyStack.push(url);
    this.historyIndex++;
  }

  private updateCanGoBack() {
    const url = this.router.url.split(/[?#]/)[0];
    const hasHistory = this.historyIndex > 0;
    const canGoBack = (this.activeFeatureId && this.rnCanGoBack) || hasHistory;
    console.log(
      `[NAV] [BackCoordinator] updateCanGoBack: url=${url}, historyIndex=${this.historyIndex}, stackSize=${this.historyStack.length}, rnCanGoBack=${this.rnCanGoBack}, result=${canGoBack}`
    );
    this.canGoBackSubject.next(canGoBack);
  }

  private handleRnNavigationStateChange = (event: Event) => {
    const detail = (event as CustomEvent).detail;
    if (!detail?.featureId) {
      return;
    }

    console.log(
      `[NAV] [BackCoordinator] Received rnNavigationStateChange: featureId=${detail.featureId}, canGoBack=${detail.canGoBack}, activeFeatureId=${this.activeFeatureId}`
    );

    // Only the foreground feature is authoritative
    if (detail.featureId === this.activeFeatureId) {
      this.rnCanGoBack = !!detail.canGoBack;
      console.log(
        `[NAV] [BackCoordinator] Updated rnCanGoBack to: ${this.rnCanGoBack}`
      );
      this.updateCanGoBack();
    } else {
      console.log(
        `[NAV] [BackCoordinator] Ignoring state from inactive feature ${detail.featureId}`
      );
    }
  };

  private handleHardwareBack() {
    const url = this.router.url.split(/[?#]/)[0];

    // If an RN feature is active and has internal back history, let it pop.
    if (this.activeFeatureId && this.rnCanGoBack) {
      console.log(
        `[NAV] [BackCoordinator] RN feature ${this.activeFeatureId} can go back; delegating to RN and leaving Angular nav as no-op.`
      );
      window.dispatchEvent(
        new CustomEvent("angularBackPressed", {
          detail: { featureId: this.activeFeatureId },
        })
      );
      return;
    }

    // If there is an actual Angular back entry, go back even on root URLs. Only
    // allow app exit when our stack is empty.
    if (this.historyIndex > 0) {
      console.log(
        `[NAV] [BackCoordinator] Angular stack has ${this.historyIndex} prior entries; calling location.back() from ${url}.`
      );
      this.location.back();
      return;
    }

    console.log(
      `[NAV] [BackCoordinator] No Angular back history; at root URL ${url} (allow app exit).`
    );
  }
}
